import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  ColDefBuilder,
  DataTable,
  Icon,
  IconProps,
  Label,
  Stack,
  Switch,
} from '@eluve/components';
import {
  AppointmentFileTypesEnum,
  UserUploadStatusEnum,
} from '@eluve/graphql-types';

import { AppointmentFileActionsColumn } from './AppointmentFileActionsColumn';
import { DeviceInfo, DeviceInfoPopover } from './DeviceInfoPopover';
import { FileIdsPopover } from './FileIdsPopover';
import { FileTag, FileTagsDialog } from './FileTagsDialog';
import { SegmentMetadataPopover } from './SegmentMetadataPopover';
import { appointmentFilesFragment, appointmentFilesQuery } from './operations';

type Row = {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  fileUrl: string | null;
  fileName: string | undefined;
  isFinalChunk: boolean;
  status: UserUploadStatusEnum | undefined;
  type: AppointmentFileTypesEnum;
  transcriptionStatus?: string;
  segmentId?: string;
  segmentStartedAt?: string;
  fileUploadedAt?: string;
  tags: FileTag[];
  metadata: Record<string, unknown> | null;
  audioStack: string | null;
  device: DeviceInfo | null;
};

const createColumns = (showAudioStack: boolean) => {
  const builder = new ColDefBuilder<Row>()
    .colDef({
      id: 'actions',
      header: ' ',
      cell: ({ row: { original } }) => (
        <Stack gap={2} direction="horizontal">
          <FileIdsPopover
            ids={[
              { label: 'File ID', value: original.fileId },
              { label: 'GCS Path', value: original.fileUrl },
              { label: 'Segment ID', value: original.segmentId },
            ]}
          />
          <SegmentMetadataPopover metadata={original.metadata} />
          <DeviceInfoPopover device={original.device} />
          <FileTagsDialog tags={original.tags} />
          <AppointmentFileActionsColumn
            tenantId={original.tenantId}
            appointmentId={original.appointmentId}
            fileId={original.fileId}
            fileName={original.fileName}
            filePath={original.fileUrl ?? ''}
            fileType={original.type}
            fileStatus={original.status}
            fileUploadedAt={original.fileUploadedAt}
            transcriptionStatus={original.transcriptionStatus}
          />
        </Stack>
      ),
    })
    .defaultSortable('type', {
      cellRenderer: (row) => {
        const iconName = match<AppointmentFileTypesEnum, IconProps['name']>(
          row.type,
        )
          .with('WEBSOCKET_AUDIO', () => 'RadioTower')
          .with('CHUNKED_AUDIO', () => 'Puzzle')
          .with('USER_BACKUP_AUDIO', () => 'AudioWaveform')
          .with('RAW_TRANSCRIPT_METADATA', () => 'FileBracesCorner')
          .otherwise(() => 'Files');

        return (
          <SimpleTooltip label={row.type}>
            <Stack
              align="center"
              className="size-10"
              justify="center"
              gap={2}
              direction="horizontal"
            >
              <Icon name={iconName} size="lg" />
            </Stack>
          </SimpleTooltip>
        );
      },
    })
    .defaultSortable('status')
    .defaultSortable('transcriptionStatus', 'Transcription')
    .defaultBoolean('isFinalChunk', 'Final');

  if (showAudioStack) {
    builder.defaultSortable('audioStack', 'Audio Stack');
  }

  builder
    .defaultSortable('fileName', 'File Name')
    .dateSortable('segmentStartedAt', 'Segment Started At')
    .dateSortable('fileUploadedAt', 'Uploaded At');

  return builder.build();
};

export const AdminAppointmentFiles: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const showAudioStack = searchParams.get('audioStack') !== 'false';

  const handleToggleAudioStack = (checked: boolean) => {
    setSearchParams((prev) => {
      if (checked) {
        prev.delete('audioStack');
      } else {
        prev.set('audioStack', 'false');
      }
      return prev;
    });
  };
  const columns = useMemo(
    () => createColumns(showAudioStack),
    [showAudioStack],
  );

  useSuspenseQuery(appointmentFilesQuery, {
    variables: { tenantId, appointmentId },
  });

  const { appointmentFiles } = useCompleteFragment({
    fragment: appointmentFilesFragment,
    key: { id: appointmentId },
  });
  const rows = useMemo(() => {
    return appointmentFiles.map<Row>((af) => {
      const segmentMetadata =
        (af.userUpload?.transcript?.appointment_segment?.metadata as Record<
          string,
          unknown
        >) ?? null;

      const hasWebsocketHashMatchTag = (af.tags ?? []).some(
        (t) =>
          t.tag.tag ===
          'SystemMetadata.UPLOAD_CANCELLED_WEBSOCKET_AUDIO_HASH_MATCH',
      );

      const transcriptionStatus =
        af.fileType === 'RAW_TRANSCRIPT_METADATA'
          ? 'N/A'
          : hasWebsocketHashMatchTag
            ? 'Transcribed Websocket Audio'
            : (af.userUpload?.transcript?.transcriptionStatus ??
              'NO TRANSCRIPT');

      return {
        tenantId,
        appointmentId,
        fileId: af.fileId,
        fileUrl: af.userUpload?.fileUrl,
        fileName: af.userUpload?.originalFileName ?? undefined,
        type: af.fileType,
        isFinalChunk: af.userUpload?.transcript?.isFinalChunk ?? false,
        status: af.userUpload?.status,
        transcriptionStatus,
        segmentId: af.userUpload?.transcript?.segmentId,
        segmentStartedAt:
          af.userUpload?.transcript?.appointment_segment?.recordingStartedAt,
        fileUploadedAt: af.userUpload?.uploadedAt ?? undefined,
        metadata: segmentMetadata,
        audioStack:
          segmentMetadata?.audioStack != null
            ? String(segmentMetadata.audioStack)
            : null,
        device: af.userUpload?.transcript?.appointment_segment?.device ?? null,
        tags: (af.tags ?? []).map((t) => ({
          id: t.id,
          tag: t.tag.tag,
          isSystemTag: t.tag.isSystemTag,
          isResolvable: t.tag.isResolvable,
          createdAt: t.createdAt,
          updatedAt: t.updatedAt,
          resolvedAt: t.resolvedAt,
          isSelfResolved: t.isSelfResolved,
          metadata: t.metadata,
          addedById: t.addedById ?? null,
          addedByUser: t.addedByUser
            ? {
                email: t.addedByUser.email,
                firstName: t.addedByUser.firstName,
                lastName: t.addedByUser.lastName,
                displayName: t.addedByUser.displayName ?? null,
              }
            : null,
        })),
      };
    });
  }, [appointmentFiles, tenantId, appointmentId]);

  return (
    <div>
      <Stack align="center" gap={2} className="mb-2" direction="horizontal">
        <Switch
          id="audio-stack-toggle"
          checked={showAudioStack}
          onCheckedChange={handleToggleAudioStack}
        />
        <Label htmlFor="audio-stack-toggle">Show Audio Stack</Label>
      </Stack>
      <DataTable
        data={rows}
        columns={columns}
        initialSortingState={[
          {
            id: 'fileName',
            desc: true,
          },
        ]}
      />
    </div>
  );
};
